import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTikTokOembedThumbnail } from './TikTokThumbnail';
import { useTikTokVideoId } from '../../hooks/useTikTokVideoId';

interface TikTokVideoCardProps {
  company: {
    id: number;
    name: string;
    industry: string;
    videoThumbnail: string;
    editorNote: string;
    tags: string[];
    publishedDays?: number;
    tiktokVideoId?: string;
    tiktokEmbedUrl?: string;
  };
  tiktokUrl?: string;
  variant?: 'normal' | 'live';
  liveSchedule?: string;
}

// グローバルな再生中のプレイヤー管理
let currentPlayingIframe: HTMLIFrameElement | null = null;
let currentSetIsPlaying: ((v: boolean) => void) | null = null;

export default function TikTokVideoCard({
  company,
  tiktokUrl,
  variant = 'normal',
  liveSchedule,
}: TikTokVideoCardProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPlayer, setShowPlayer] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // oEmbed APIでサムネイルを取得（tiktokUrlがある場合）
  const { src: oembedThumbnail, ready: thumbnailReady } = useTikTokOembedThumbnail(
    tiktokUrl || '',
    company.videoThumbnail
  );
  const thumbnailSrc = tiktokUrl ? oembedThumbnail : company.videoThumbnail;

  // tiktokVideoId が空で tiktokEmbedUrl がある場合、oEmbedで動画IDを解決する
  const needsIdResolution = !company.tiktokVideoId && !!company.tiktokEmbedUrl;
  const { videoId: resolvedVideoId } = useTikTokVideoId(needsIdResolution ? (company.tiktokEmbedUrl ?? '') : '');

  // 使用する動画ID：直接指定 > 解決済み
  const effectiveVideoId = company.tiktokVideoId || resolvedVideoId;

  // 埋め込みURL：有効な動画IDがあればPlayer v1 APIを使用
  const embedUrl = effectiveVideoId
    ? `https://www.tiktok.com/player/v1/${effectiveVideoId}?controls=1&play_button=1&music_info=0&description=0`
    : null;

  const handleMediaClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!embedUrl || loadError) {
      if (tiktokUrl) {
        window.open(tiktokUrl, '_blank', 'noopener,noreferrer');
      }
      return;
    }

    if (!showPlayer) {
      if (currentPlayingIframe && currentSetIsPlaying) {
        currentPlayingIframe.contentWindow?.postMessage(
          { type: 'pause', value: null, 'x-tiktok-player': true },
          '*'
        );
        currentSetIsPlaying(false);
        currentPlayingIframe = null;
        currentSetIsPlaying = null;
      }
      setShowPlayer(true);
      setIsPlaying(true);
      return;
    }

    if (isPlaying) {
      iframeRef.current?.contentWindow?.postMessage(
        { type: 'pause', value: null, 'x-tiktok-player': true },
        '*'
      );
      setIsPlaying(false);
      if (currentPlayingIframe === iframeRef.current) {
        currentPlayingIframe = null;
        currentSetIsPlaying = null;
      }
    } else {
      if (currentPlayingIframe && currentPlayingIframe !== iframeRef.current && currentSetIsPlaying) {
        currentPlayingIframe.contentWindow?.postMessage(
          { type: 'pause', value: null, 'x-tiktok-player': true },
          '*'
        );
        currentSetIsPlaying(false);
      }
      iframeRef.current?.contentWindow?.postMessage(
        { type: 'play', value: null, 'x-tiktok-player': true },
        '*'
      );
      setIsPlaying(true);
      currentPlayingIframe = iframeRef.current;
      currentSetIsPlaying = setIsPlaying;
    }
  };

  const handleIframeLoad = () => {
    if (iframeRef.current) {
      currentPlayingIframe = iframeRef.current;
      currentSetIsPlaying = setIsPlaying;
      // iframeロード完了後にplayコマンドを送信
      setTimeout(() => {
        iframeRef.current?.contentWindow?.postMessage(
          { type: 'play', value: null, 'x-tiktok-player': true },
          '*'
        );
      }, 300);
    }
  };

  const handleIframeError = () => {
    setLoadError(true);
    setIsPlaying(false);
  };

  return (
    <div className="group cursor-pointer block transition-transform duration-300 hover:-translate-y-1">
      <div
        className="relative rounded-2xl overflow-hidden mb-3 aspect-[9/16] bg-gray-100 shadow-sm hover:shadow-lg transition-shadow duration-300"
        onClick={handleMediaClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* サムネイル（プレイヤー非表示時） */}
        {!showPlayer && (
          <>
            {!thumbnailReady && !thumbnailSrc ? (
              <div className="w-full h-full bg-gray-200 animate-pulse flex items-center justify-center">
                <i className="ri-video-line text-gray-400 text-3xl"></i>
              </div>
            ) : (
              <img
                src={thumbnailSrc}
                alt={company.name}
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
            )}
          </>
        )}

        {/* TikTok Embed Player */}
        {embedUrl && showPlayer && (
          <iframe
            ref={iframeRef}
            src={embedUrl}
            className="absolute inset-0 w-full h-full"
            allow="autoplay; fullscreen"
            allowFullScreen
            loading="lazy"
            onError={handleIframeError}
            onLoad={handleIframeLoad}
            style={{ border: 'none' }}
          />
        )}

        {/* オーバーレイ（サムネイル表示時のみ） */}
        {!showPlayer && (
          <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-black/10 group-hover:from-black/10 group-hover:to-black/20 transition-all duration-300" />
        )}

        {/* ▶ 再生アイコン：未再生 */}
        {!loadError && !showPlayer && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-16 h-16 flex items-center justify-center bg-white/95 rounded-full shadow-xl group-hover:scale-110 group-hover:bg-white transition-all duration-300">
              <i className="ri-play-fill text-3xl text-gray-900 ml-1"></i>
            </div>
          </div>
        )}

        {/* ▶ 再生アイコン：停止中 */}
        {!loadError && showPlayer && !isPlaying && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-16 h-16 flex items-center justify-center bg-black/70 backdrop-blur-sm rounded-full shadow-xl transition-all duration-300">
              <i className="ri-play-fill text-3xl text-white ml-1"></i>
            </div>
          </div>
        )}

        {/* ⏸ 一時停止アイコン：再生中かつホバー時 */}
        {!loadError && showPlayer && isPlaying && isHovered && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-16 h-16 flex items-center justify-center bg-black/70 backdrop-blur-sm rounded-full shadow-xl transition-all duration-300">
              <i className="ri-pause-fill text-3xl text-white"></i>
            </div>
          </div>
        )}

        {/* エラー時 */}
        {loadError && tiktokUrl && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm">
            <div className="text-center px-6 py-4">
              <i className="ri-error-warning-line text-4xl text-white mb-3 block"></i>
              <p className="text-white text-sm mb-3 font-medium">動画を読み込めませんでした</p>
              <span className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-gray-900 rounded-full text-sm font-medium hover:bg-gray-100 transition-colors">
                <i className="ri-tiktok-fill text-lg"></i>
                TikTokで見る
              </span>
            </div>
          </div>
        )}

        {/* 業界ラベル */}
        {variant === 'normal' && (
          <div className="absolute top-3 left-3 bg-black/75 backdrop-blur-md px-3 py-1.5 rounded-full text-xs text-white font-medium z-10 shadow-sm">
            {company.industry}
          </div>
        )}

        {/* LIVE時刻情報 */}
        {variant === 'live' && liveSchedule && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-4 z-10">
            <p className="text-white text-sm font-semibold">{liveSchedule}</p>
          </div>
        )}
      </div>

      {/* 導線領域 */}
      <div className="px-1">
        <h3 className="font-bold text-sm mb-1.5 line-clamp-1 text-gray-900">
          {tiktokUrl ? (
            <a
              href={tiktokUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-gray-700 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              {company.name}
            </a>
          ) : (
            <Link to={`/company/${company.id}`} className="hover:text-gray-700 transition-colors">
              {company.name}
            </Link>
          )}
        </h3>

        <p className="text-xs text-gray-600 mb-2 line-clamp-2 leading-relaxed">{company.editorNote}</p>

        {variant === 'normal' && (
          <div className="flex flex-wrap gap-1.5">
            {company.tags.slice(0, 2).map((tag, idx) => (
              <span key={idx} className="text-xs text-gray-500 bg-gray-50 px-2 py-0.5 rounded-md">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
